import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faFilter,
  faTruck,
  faCheckCircle,
  faTimes,
  faBox,
  faCalendarAlt,
  faTrash,
  faCopy,
  faListAlt
} from '@fortawesome/free-solid-svg-icons';
import { formatTrackingCode } from '@/utils/formatters';

const STORAGE_KEY = 'agentSavedShipments';

const getStatusColor = (status) => {
  switch (status) {
    case 'Pending':
    case 'Pending Approval':
      return 'bg-yellow-100 text-yellow-800';
    case 'Processing':
    case 'Shipped':
    case 'In Transit':
      return 'bg-blue-100 text-blue-800';
    case 'Delivered':
      return 'bg-green-100 text-green-800';
    case 'Returned':
    case 'Rejected':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Pending':
    case 'Pending Approval':
      return faBox;
    case 'Processing':
    case 'Shipped':
    case 'In Transit':
      return faTruck;
    case 'Delivered':
      return faCheckCircle;
    case 'Returned':
    case 'Rejected':
      return faTimes;
    default:
      return faBox;
  }
};

const SavedShipments = () => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      setSaved(Array.isArray(parsed) ? parsed : []);
    } catch {
      setSaved([]);
    }
  }, []);

  const clearAll = () => {
    setSaved([]);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify([])); } catch {}
  };

  const removeOne = (id) => {
    const next = saved.filter(s => s.id !== id);
    setSaved(next);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  };

  const filtered = saved.filter(s => {
    if (filter === 'all') return true;
    return s.status === filter;
  });

  const pendingCount = saved.filter(s => s.status === 'Pending' || s.status === 'Pending Approval').length;
  const inTransitCount = saved.filter(s => ['Processing','Shipped','In Transit'].includes(s.status)).length;
  const deliveredCount = saved.filter(s => s.status === 'Delivered').length;
  const rejectedCount = saved.filter(s => s.status === 'Returned' || s.status === 'Rejected').length;

  return (
    <div>
      {/* Back to Dashboard */}
      <div className="mb-4">
        <Link
          to="/dashboard/agent"
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Dashboard
        </Link>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faListAlt} className="text-gray-500" />
            <h1 className="text-2xl font-bold text-gray-900">Saved Shipments</h1>
            <span className="ml-2 text-xs px-2 py-1 rounded bg-gray-100 text-gray-700">{saved.length}</span>
          </div>
          {saved.length > 0 && (
            <button
              onClick={clearAll}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
            >
              <FontAwesomeIcon icon={faTrash} className="mr-2" />
              Clear All
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-yellow-500 cursor-pointer" onClick={() => setFilter('Pending')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-yellow-100 text-yellow-600 mr-3">
                  <FontAwesomeIcon icon={faBox} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-xl font-bold text-gray-900">{pendingCount}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500 cursor-pointer" onClick={() => setFilter('In Transit')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600 mr-3">
                  <FontAwesomeIcon icon={faTruck} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">In Transit</p>
                  <p className="text-xl font-bold text-gray-900">{inTransitCount}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500 cursor-pointer" onClick={() => setFilter('Delivered')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100 text-green-600 mr-3">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivered</p>
                  <p className="text-xl font-bold text-gray-900">{deliveredCount}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 border-l-4 border-red-500 cursor-pointer" onClick={() => setFilter('Rejected')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-red-100 text-red-600 mr-3">
                  <FontAwesomeIcon icon={faTimes} />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rejected</p>
                  <p className="text-xl font-bold text-gray-900">{rejectedCount}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex items-center gap-4">
            <FontAwesomeIcon icon={faFilter} className="text-gray-400" />
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Transit">In Transit</option>
              <option value="Delivered">Delivered</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Saved List */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <FontAwesomeIcon icon={faBox} className="text-red-500 text-2xl" />
              </div>
            </div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No saved shipments found</h3>
            <p className="text-gray-500 mb-6">Use “Open Saved Page” from Manage Shipments or save when viewing details.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((s) => (
              <div key={s.id} className={`bg-white rounded-lg shadow overflow-hidden border-l-4 transition-all duration-300 hover:shadow-md ${
                s.status === 'Pending Approval' ? 'border-yellow-500' :
                s.status === 'In Transit' ? 'border-blue-500' :
                s.status === 'Delivered' ? 'border-green-500' :
                s.status === 'Rejected' ? 'border-red-500' :
                'border-gray-200'
              }`}>
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{s.id}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(s.status)}`}>{s.status}</span>
                      </div>
                      {s.trackingCode && (
                        <p className="text-xs text-gray-500 flex items-center mb-2">
                          <FontAwesomeIcon icon={faBox} className="mr-1" />
                          Tracking: <span className="ml-1 font-medium text-gray-900">{formatTrackingCode(s.trackingCode)}</span>
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(formatTrackingCode(s.trackingCode))}
                            className="ml-2 p-1 rounded hover:bg-gray-100"
                            title="Copy tracking ID"
                          >
                            <FontAwesomeIcon icon={faCopy} className="text-gray-400 hover:text-gray-600" />
                          </button>
                        </p>
                      )}
                      {s.date && (
                        <p className="text-sm text-gray-500 flex items-center mb-3">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-1" />
                          {s.date}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => navigate('/dashboard/agent/tracking', { state: { trackingCode: formatTrackingCode(s.trackingCode || s.id || '') } })}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center transition-colors"
                      >
                        <FontAwesomeIcon icon={faTruck} className="mr-2" />
                        Update Tracking
                      </button>
                      <button
                        onClick={() => removeOne(s.id)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedShipments;